// Bringing the old books in (gap #17), in Books › Setup: the old chart of accounts first
// (imports.chartOfAccounts: an account whose code is already here is left as it is), the opening
// balances at the switch-over date
// (imports.openingBalances), which post only after they tie out, and monthly history summaries
// (imports.historySummaries), kept as report comparatives and never posted. The screen asks for every
// tie-out figure before it sends; the server's own tie-out check is shown, then what posted.

import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { useDesign } from '../blueprint/design.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import {
  CHART_TYPES,
  chartRows,
  chartTypeLabel,
  HISTORY_METRICS,
  HISTORY_SOURCES,
  historyRows,
  sourceCode,
  tieCents,
  trialRows,
} from './corrections-model.ts';
import { dayText } from './model.ts';
import type { Account } from './types.ts';
import { Money, Panel, errorText, useQuery } from './ui.tsx';
import { DocLink } from '../shared/DocLink.tsx';
import '../shared/office-lists.css';
import './corrections.css';

interface TieOut {
  balanced: boolean;
  checks: { what: string; expectedCents: number; importedCents: number; ok: boolean }[];
}

/** A CSV picked as a file, or pasted: either fills the same box. */
function CsvBox({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (text: string, fileName?: string) => void;
}) {
  return (
    <div className="csv-box full">
      <label>
        <span className="flabel">{label}</span>
        <textarea
          className="field area"
          rows={6}
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="muted small">{hint}</span>
      </label>
      <label className="csv-file">
        <span className="flabel">Or pick the file</span>
        <input
          className="field"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void f.text().then((t) => onChange(t, f.name));
          }}
        />
      </label>
    </div>
  );
}

function TieOutTable({ tieOut }: { tieOut: TieOut }) {
  return (
    <div className="table-wrap">
      <table className="grid-table" aria-label="The tie-out">
        <thead>
          <tr>
            <th>Check</th>
            <th className="num">The old books say</th>
            <th className="num">This import has</th>
            <th>Ties out</th>
          </tr>
        </thead>
        <tbody>
          {tieOut.checks.map((c) => (
            <tr key={c.what}>
              <td>{c.what}</td>
              <td className="num">
                <Money cents={c.expectedCents} />
              </td>
              <td className="num">
                <Money cents={c.importedCents} />
              </td>
              <td>
                <span className={`chip say ${c.ok ? 'ok' : 'bad'}`}>{c.ok ? 'Yes' : 'No'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Imports({
  toast,
  canChart,
  onChart,
}: {
  toast: (m: string) => void;
  /** accounting.setup: bring in the old chart of accounts. */
  canChart: boolean;
  /** The chart changed: Setup reads it again. */
  onChart: () => Promise<void>;
}) {
  const body = (
    <>
      <h2 id="imports-title">Bring the old books in</h2>
      {canChart && <ChartImport toast={toast} onChart={onChart} />}
      <OpeningBalances toast={toast} />
      <History toast={toast} />
    </>
  );
  // In Blueprint its three panels are sections of the page, so it holds them without being one itself.
  if (useDesign() === 'blueprint')
    return (
      <div className="imports" role="region" aria-labelledby="imports-title">
        {body}
      </div>
    );
  return (
    <section className="imports" aria-labelledby="imports-title">
      {body}
    </section>
  );
}

function ChartImport({ toast, onChart }: { toast: (m: string) => void; onChart: () => Promise<void> }) {
  const [picked, setPicked] = useState<string>(HISTORY_SOURCES[0].code);
  const [other, setOther] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const parsed = chartRows(csv);
  const source = picked === 'other' ? sourceCode(other) : picked;
  const ready = source !== null && parsed.rows.length > 0 && !parsed.problems.length;
  const send = async () => {
    if (!ready || !source) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ created: number; existing: number }>('imports.chartOfAccounts', {
        source,
        rows: parsed.rows,
      });
      const said =
        `Added ${r.created} ${r.created === 1 ? 'account' : 'accounts'} to the chart.` +
        (r.existing
          ? ` ${r.existing} ${r.existing === 1 ? 'was' : 'were'} already here by code and stayed as ${r.existing === 1 ? 'it was' : 'they were'}.`
          : '');
      setDone(said);
      toast(said);
      await onChart();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Chart of accounts">
      <p className="muted">
        The old books’ accounts, before their opening balances: an account whose code is already in the chart
        stays as it is. Rename or archive them after in the chart above.
      </p>
      <form
        className="fgrid"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void send();
        }}
      >
        <label>
          <span className="flabel">Where they came from</span>
          <select className="field" value={picked} onChange={(e) => setPicked(e.target.value)}>
            {HISTORY_SOURCES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
            <option value="other">Another system</option>
          </select>
        </label>
        {picked === 'other' && (
          <label>
            <span className="flabel">The system’s name</span>
            <input
              className="field"
              maxLength={60}
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
            {other.trim() && !source && <span className="small bad-ink">The name needs some letters.</span>}
          </label>
        )}
        <CsvBox
          label="Accounts (code, name, type, old id)"
          hint={`One account a line: 6110, Truck fuel, Expense. The type is the old system’s word or ours (${CHART_TYPES.map((t) => t.label).join(', ')}); the old id is optional.`}
          value={csv}
          onChange={(t) => {
            setCsv(t);
            setDone(null);
          }}
        />
        {csv.trim() && (
          <div className="full">
            <p className={`notice${parsed.problems.length ? ' warn' : ' ok'}`}>
              {parsed.rows.length} {parsed.rows.length === 1 ? 'account' : 'accounts'} ready
              {parsed.problems.length > 0 && (
                <span className="block">{parsed.problems.slice(0, 5).join(' ')}</span>
              )}
            </p>
            {parsed.rows.length > 0 && (
              <div className="table-wrap">
                <table className="grid-table" aria-label="The accounts to add">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Type here</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 12).map((r) => (
                      <tr key={r.code}>
                        <td className="code">{r.code}</td>
                        <td>{r.name}</td>
                        <td>{chartTypeLabel(r.subtype)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 12 && <p className="muted small">and {parsed.rows.length - 12} more</p>}
              </div>
            )}
          </div>
        )}
        <div className="row-actions">
          <SaveButton
            busy={busy}
            busyLabel="Adding them…"
            error={error}
            disabled={!ready}
            onClick={() => void send()}
          >
            Add the accounts
          </SaveButton>
        </div>
      </form>
      {done && (
        <p className="notice ok" role="status">
          {done}
        </p>
      )}
    </Panel>
  );
}

function OpeningBalances({ toast }: { toast: (m: string) => void }) {
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const banks = (accounts.data?.accounts ?? []).filter((a) => a.subtype === 'bank');
  const [asOf, setAsOf] = useState('');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | undefined>();
  const [ar, setAr] = useState('');
  const [ap, setAp] = useState('');
  const [deferred, setDeferred] = useState('');
  const [statements, setStatements] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    tieOut: TieOut;
    posted: string | null;
    entry: { entryId: string; number: string; postingDate: string } | null;
    ok: boolean;
  } | null>(null);
  const parsed = trialRows(csv);
  const diff = parsed.debitCents - parsed.creditCents;
  const figures = [ar, ap, deferred, ...banks.map((b) => statements[b.code] ?? '')];
  const missing = [
    ...(!asOf ? ['the switch-over date'] : []),
    ...(!parsed.rows.length ? ['the trial balance'] : []),
    ...(tieCents(ar) === null ? ['the receivables aging total'] : []),
    ...(tieCents(ap) === null ? ['the payables aging total'] : []),
    ...(tieCents(deferred) === null ? ['the unused memberships (deferred revenue)'] : []),
    ...banks
      .filter((b) => tieCents(statements[b.code] ?? '') === null)
      .map((b) => `${b.code} ${b.name}’s statement balance`),
  ];
  const ready = !missing.length && !parsed.problems.length && diff === 0 && figures.every((f) => f.trim());
  const send = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await client.call<{
        tieOut: TieOut;
        entry: { entryId: string; number: string; postingDate: string } | null;
      }>('imports.openingBalances', {
        asOf,
        rows: parsed.rows,
        tieOut: {
          arAgingCents: tieCents(ar)!,
          apAgingCents: tieCents(ap)!,
          deferredRevenueCents: tieCents(deferred)!,
          bankStatements: banks.map((b) => ({
            accountCode: b.code,
            statementCents: tieCents(statements[b.code]!)!,
          })),
        },
        ...(fileName ? { fileName } : {}),
      });
      const posted = r.entry
        ? `Posted ${r.entry.number} on ${dayText(r.entry.postingDate)}.`
        : 'Nothing changed: these balances were already in at this date.';
      setResult({ tieOut: r.tieOut, posted, entry: r.entry, ok: true });
      toast(posted);
    } catch (e) {
      // A failed tie-out says which checks, and its table comes with it.
      const tie = e instanceof RpcError ? (e.detail as { tieOut?: TieOut } | null)?.tieOut : undefined;
      if (tie) setResult({ tieOut: tie, posted: null, entry: null, ok: false });
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Opening balances">
      <p className="muted">
        The old books’ trial balance at the switch-over date. It posts only once it balances and ties out to
        the old aging reports, the unused memberships and each bank statement. Importing again for the same
        date (the accountant’s adjusted file, say) posts only the differences.
      </p>
      <form
        className="fgrid"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void send();
        }}
      >
        <label>
          <span className="flabel">Switch-over date</span>
          <input className="field" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </label>
        <CsvBox
          label="Trial balance (account code, debit, credit)"
          hint="One account a line, in dollars: 1000, 12500.00, then a blank credit. The codes must be in your chart: bring the old chart in above first."
          value={csv}
          onChange={(t, name) => {
            setCsv(t);
            if (name) setFileName(name);
            setResult(null);
          }}
        />
        {csv.trim() && (
          <p className={`full notice${parsed.problems.length || diff ? ' warn' : ' ok'}`}>
            {parsed.rows.length} {parsed.rows.length === 1 ? 'account' : 'accounts'} · debits{' '}
            <Money cents={parsed.debitCents} /> · credits <Money cents={parsed.creditCents} />
            {diff ? (
              <>
                {' '}
                · off by <Money cents={Math.abs(diff)} />: it must balance
              </>
            ) : (
              ' · balances'
            )}
            {parsed.problems.length > 0 && (
              <span className="block">{parsed.problems.slice(0, 5).join(' ')}</span>
            )}
          </p>
        )}
        <h3 className="full">The tie-out (all required)</h3>
        <label>
          <span className="flabel">Receivables aging total</span>
          <input className="field" inputMode="decimal" value={ar} onChange={(e) => setAr(e.target.value)} />
        </label>
        <label>
          <span className="flabel">Payables aging total</span>
          <input className="field" inputMode="decimal" value={ap} onChange={(e) => setAp(e.target.value)} />
        </label>
        <label>
          <span className="flabel">Unused memberships (deferred revenue)</span>
          <input
            className="field"
            inputMode="decimal"
            value={deferred}
            onChange={(e) => setDeferred(e.target.value)}
          />
        </label>
        {banks.map((b) => (
          <label key={b.id}>
            <span className="flabel">
              {b.code} {b.name}: statement balance
            </span>
            <input
              className="field"
              inputMode="decimal"
              value={statements[b.code] ?? ''}
              onChange={(e) => setStatements((s) => ({ ...s, [b.code]: e.target.value }))}
            />
          </label>
        ))}
        {missing.length > 0 && <p className="full muted small">Still needed: {missing.join(', ')}.</p>}
        <div className="row-actions">
          <SaveButton
            busy={busy}
            busyLabel="Checking and posting…"
            error={error}
            disabled={!ready}
            onClick={() => void send()}
          >
            Check and post the opening balances
          </SaveButton>
        </div>
      </form>
      {result && (
        <div className="import-result">
          <h3>{result.ok ? 'It tied out' : 'It didn’t tie out: nothing posted'}</h3>
          <TieOutTable tieOut={result.tieOut} />
          {result.posted && (
            <p className="notice ok" role="status">
              {result.entry ? (
                <>
                  Posted{' '}
                  <DocLink doc={{ kind: 'journal-entry', id: result.entry.entryId }}>
                    {result.entry.number}
                  </DocLink>{' '}
                  on {dayText(result.entry.postingDate)}.
                </>
              ) : (
                result.posted
              )}
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}

function History({ toast }: { toast: (m: string) => void }) {
  const units = useQuery<{ businessUnits: { id: string; name: string }[] }>('businessUnits.list', {});
  const [picked, setPicked] = useState<string>(HISTORY_SOURCES[0].code);
  const [other, setOther] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const parsed = historyRows(csv, units.data?.businessUnits ?? []);
  // A system from the list, or another one by name: the code is made from what they type.
  const source = picked === 'other' ? sourceCode(other) : picked;
  const ready = source !== null && parsed.rows.length > 0 && !parsed.problems.length;
  const send = async () => {
    if (!ready || !source) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ months: number; rows: number }>('imports.historySummaries', {
        source,
        rows: parsed.rows,
      });
      const said = `Kept ${r.rows} ${r.rows === 1 ? 'figure' : 'figures'} for ${r.months} ${r.months === 1 ? 'month' : 'months'}. Reports show them beside this year’s.`;
      setDone(said);
      toast(said);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="History summaries">
      <p className="muted">
        Monthly totals from the old system, kept so reports can compare this year with last. They are never
        posted to the ledger. Importing a month again replaces its figures.
      </p>
      <form
        className="fgrid"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void send();
        }}
      >
        <label>
          <span className="flabel">Where they came from</span>
          <select className="field" value={picked} onChange={(e) => setPicked(e.target.value)}>
            {HISTORY_SOURCES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
            <option value="other">Another system</option>
          </select>
        </label>
        {picked === 'other' && (
          <label>
            <span className="flabel">The system’s name</span>
            <input
              className="field"
              maxLength={60}
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
            {other.trim() && !source && <span className="small bad-ink">The name needs some letters.</span>}
          </label>
        )}
        <CsvBox
          label="Totals (month, figure, amount, department)"
          hint={`One figure a line: 2025-09, Revenue, 48250.00, Drains. The figures: ${HISTORY_METRICS.map((m) => m.label.toLowerCase()).join(', ')}. The department is optional.`}
          value={csv}
          onChange={(t) => {
            setCsv(t);
            setDone(null);
          }}
        />
        {csv.trim() && (
          <p className={`full notice${parsed.problems.length ? ' warn' : ' ok'}`}>
            {parsed.rows.length} {parsed.rows.length === 1 ? 'figure' : 'figures'} ready
            {parsed.problems.length > 0 && (
              <span className="block">{parsed.problems.slice(0, 5).join(' ')}</span>
            )}
          </p>
        )}
        <div className="row-actions">
          <SaveButton
            busy={busy}
            busyLabel="Keeping them…"
            error={error}
            disabled={!ready}
            onClick={() => void send()}
          >
            Keep the history
          </SaveButton>
        </div>
      </form>
      {done && (
        <p className="notice ok" role="status">
          {done}
        </p>
      )}
    </Panel>
  );
}
